/**
 * BodyRegionPicker Component
 *
 * Interactive SVG body outline for athletes to log regional pain.
 * Front + back silhouettes with tappable regions, inline pain level
 * slider and pain type selector.  Mobile-optimized, collapsible.
 *
 * @component
 */

import React, { useState } from 'react';
import type { BodyRegion, PainType, RegionalPainEntry } from '@/types/sessions';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const BODY_REGIONS: { id: BodyRegion; label: string; view: 'front' | 'back' }[] = [
  { id: 'head_neck', label: 'Head / Neck', view: 'front' },
  { id: 'left_shoulder', label: 'L Shoulder', view: 'front' },
  { id: 'right_shoulder', label: 'R Shoulder', view: 'front' },
  { id: 'upper_back', label: 'Upper Back', view: 'back' },
  { id: 'lower_back', label: 'Lower Back', view: 'back' },
  { id: 'left_hip', label: 'L Hip', view: 'front' },
  { id: 'right_hip', label: 'R Hip', view: 'front' },
  { id: 'left_quad', label: 'L Quad', view: 'front' },
  { id: 'right_quad', label: 'R Quad', view: 'front' },
  { id: 'left_hamstring', label: 'L Hamstring', view: 'back' },
  { id: 'right_hamstring', label: 'R Hamstring', view: 'back' },
  { id: 'left_knee', label: 'L Knee', view: 'front' },
  { id: 'right_knee', label: 'R Knee', view: 'front' },
  { id: 'left_ankle', label: 'L Ankle', view: 'front' },
  { id: 'right_ankle', label: 'R Ankle', view: 'front' },
  { id: 'left_calf', label: 'L Calf', view: 'back' },
  { id: 'right_calf', label: 'R Calf', view: 'back' },
  { id: 'other', label: 'Other', view: 'front' },
];

const PAIN_TYPES: { id: PainType; label: string }[] = [
  { id: 'soreness', label: 'Soreness' },
  { id: 'sharp', label: 'Sharp' },
  { id: 'tightness', label: 'Tightness' },
  { id: 'ache', label: 'Ache' },
];

/** Hit-area positions for each region on the 100x200 silhouette. */
const FRONT_POSITIONS: Record<string, { x: number; y: number; w: number; h: number }> = {
  head_neck:       { x: 50, y: 18, w: 26, h: 30 },
  left_shoulder:   { x: 26, y: 52, w: 18, h: 16 },
  right_shoulder:  { x: 74, y: 52, w: 18, h: 16 },
  left_hip:        { x: 36, y: 105, w: 16, h: 14 },
  right_hip:       { x: 64, y: 105, w: 16, h: 14 },
  left_quad:       { x: 37, y: 128, w: 14, h: 28 },
  right_quad:      { x: 63, y: 128, w: 14, h: 28 },
  left_knee:       { x: 37, y: 158, w: 13, h: 12 },
  right_knee:      { x: 63, y: 158, w: 13, h: 12 },
  left_ankle:      { x: 37, y: 188, w: 12, h: 10 },
  right_ankle:     { x: 63, y: 188, w: 12, h: 10 },
};

const BACK_POSITIONS: Record<string, { x: number; y: number; w: number; h: number }> = {
  upper_back:      { x: 50, y: 62, w: 28, h: 22 },
  lower_back:      { x: 50, y: 88, w: 26, h: 18 },
  left_hamstring:  { x: 37, y: 134, w: 14, h: 28 },
  right_hamstring: { x: 63, y: 134, w: 14, h: 28 },
  left_calf:       { x: 37, y: 170, w: 13, h: 24 },
  right_calf:      { x: 63, y: 170, w: 13, h: 24 },
};

function painColor(level: number): string {
  if (level <= 3) return '#22c55e';
  if (level <= 5) return '#eab308';
  if (level <= 7) return '#f97316';
  return '#ef4444';
}

// ---------------------------------------------------------------------------
// Body Silhouette
// ---------------------------------------------------------------------------

interface SilhouetteProps {
  fillColor: string;
  opacity: number;
}

const BodySilhouette: React.FC<SilhouetteProps> = ({ fillColor, opacity }) => (
  <g fill={fillColor} opacity={opacity}>
    <ellipse cx="50" cy="18" rx="12" ry="14" />
    <rect x="45" y="32" width="10" height="8" rx="2" />
    <rect x="33" y="40" width="34" height="60" rx="5" />
    <rect x="20" y="44" width="11" height="42" rx="4" />
    <rect x="69" y="44" width="11" height="42" rx="4" />
    <rect x="34" y="102" width="14" height="54" rx="4" />
    <rect x="52" y="102" width="14" height="54" rx="4" />
    <rect x="34" y="158" width="13" height="36" rx="4" />
    <rect x="53" y="158" width="13" height="36" rx="4" />
  </g>
);

// ---------------------------------------------------------------------------
// Tappable Region Overlays
// ---------------------------------------------------------------------------

interface TappableRegionsProps {
  regions: { id: BodyRegion; label: string }[];
  positions: Record<string, { x: number; y: number; w: number; h: number }>;
  entryMap: Map<BodyRegion, RegionalPainEntry>;
  editingRegion: BodyRegion | null;
  onTap: (regionId: BodyRegion) => void;
}

const TappableRegions: React.FC<TappableRegionsProps> = ({
  regions, positions, entryMap, editingRegion, onTap,
}) => (
  <>
    {regions.map(region => {
      const pos = positions[region.id];
      if (!pos) return null;
      const entry = entryMap.get(region.id);
      const isEditing = editingRegion === region.id;
      return (
        <g key={region.id} onClick={() => onTap(region.id)} style={{ cursor: 'pointer' }}>
          <rect
            x={pos.x - pos.w / 2}
            y={pos.y - pos.h / 2}
            width={pos.w}
            height={pos.h}
            rx={3}
            fill={entry ? painColor(entry.pain_level) : 'transparent'}
            fillOpacity={entry ? 0.4 : 0}
            stroke={isEditing ? '#3b82f6' : entry ? painColor(entry.pain_level) : '#6b7280'}
            strokeWidth={isEditing ? 2 : entry ? 1 : 0.5}
            strokeDasharray={entry ? 'none' : '2,2'}
            className="hover:fill-gray-300 dark:hover:fill-gray-600 hover:fill-opacity-30 transition-colors"
          />
          {entry && (
            <text
              x={pos.x}
              y={pos.y + 3}
              textAnchor="middle"
              fontSize="9"
              fontWeight="bold"
              fill={painColor(entry.pain_level)}
            >
              {entry.pain_level}
            </text>
          )}
        </g>
      );
    })}
  </>
);

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface BodyRegionPickerProps {
  value: RegionalPainEntry[];
  onChange: (entries: RegionalPainEntry[]) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const BodyRegionPicker: React.FC<BodyRegionPickerProps> = ({ value, onChange }) => {
  const [editingRegion, setEditingRegion] = useState<BodyRegion | null>(null);

  const entryMap = new Map(value.map(e => [e.body_region, e]));

  const handleRegionTap = (regionId: BodyRegion) => {
    if (entryMap.has(regionId)) {
      setEditingRegion(regionId);
    } else {
      const newEntry: RegionalPainEntry = {
        body_region: regionId,
        pain_level: 5,
        pain_type: 'soreness',
      };
      onChange([...value, newEntry]);
      setEditingRegion(regionId);
    }
  };

  const handleRemoveRegion = (regionId: BodyRegion) => {
    onChange(value.filter(e => e.body_region !== regionId));
    if (editingRegion === regionId) setEditingRegion(null);
  };

  const handleUpdateEntry = (regionId: BodyRegion, updates: Partial<RegionalPainEntry>) => {
    onChange(value.map(e =>
      e.body_region === regionId ? { ...e, ...updates } : e,
    ));
  };

  const editingEntry = editingRegion ? entryMap.get(editingRegion) : null;

  const frontRegions = BODY_REGIONS.filter(r => r.view === 'front' && r.id !== 'other');
  const backRegions = BODY_REGIONS.filter(r => r.view === 'back');

  const silFill = '#9ca3af'; // neutral gray for both light/dark

  return (
    <div className="mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Tap a body region to log pain
        </span>
        <span className="text-xs text-gray-500">(optional)</span>
      </div>

      <div className="border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Dual SVG Bodies */}
          <div className="flex-shrink-0 flex gap-2 mx-auto md:mx-0">
            {/* Front view */}
            <div className="text-center">
              <svg
                viewBox="0 0 100 200"
                className="w-[100px] h-[200px] md:w-[110px] md:h-[220px]"
                aria-label="Front body region selector"
              >
                <BodySilhouette fillColor={silFill} opacity={0.25} />
                <TappableRegions
                  regions={frontRegions}
                  positions={FRONT_POSITIONS}
                  entryMap={entryMap}
                  editingRegion={editingRegion}
                  onTap={handleRegionTap}
                />
              </svg>
              <span className="text-[10px] text-gray-400 dark:text-gray-500 block mt-0.5">Front</span>
            </div>

            {/* Back view */}
            <div className="text-center">
              <svg
                viewBox="0 0 100 200"
                className="w-[100px] h-[200px] md:w-[110px] md:h-[220px]"
                aria-label="Back body region selector"
              >
                <BodySilhouette fillColor={silFill} opacity={0.25} />
                <TappableRegions
                  regions={backRegions}
                  positions={BACK_POSITIONS}
                  entryMap={entryMap}
                  editingRegion={editingRegion}
                  onTap={handleRegionTap}
                />
              </svg>
              <span className="text-[10px] text-gray-400 dark:text-gray-500 block mt-0.5">Back</span>
            </div>
          </div>

          {/* Editor panel */}
          <div className="flex-1 min-w-0">
            {editingEntry && editingRegion ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                    {BODY_REGIONS.find(r => r.id === editingRegion)?.label}
                  </h4>
                  <button
                    type="button"
                    onClick={() => handleRemoveRegion(editingRegion)}
                    className="text-xs text-red-500 hover:text-red-700"
                  >
                    Remove
                  </button>
                </div>

                {/* Pain level slider */}
                <div>
                  <label className="text-xs text-gray-600 dark:text-gray-400 block mb-1">
                    Pain Level: <span className="font-bold" style={{ color: painColor(editingEntry.pain_level) }}>{editingEntry.pain_level}</span>/10
                  </label>
                  <input
                    type="range"
                    min={1}
                    max={10}
                    value={editingEntry.pain_level}
                    onChange={(e) => handleUpdateEntry(editingRegion, { pain_level: parseInt(e.target.value) })}
                    className="metric-slider w-full h-2 cursor-pointer"
                    style={{
                      '--slider-color': painColor(editingEntry.pain_level),
                      background: `linear-gradient(to right, ${painColor(editingEntry.pain_level)} 0%, ${painColor(editingEntry.pain_level)} ${((editingEntry.pain_level - 1) / 9) * 100}%, var(--card-border, #374151) ${((editingEntry.pain_level - 1) / 9) * 100}%, var(--card-border, #374151) 100%)`,
                    } as React.CSSProperties}
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>Mild</span>
                    <span>Severe</span>
                  </div>
                </div>

                {/* Pain type chips */}
                <div>
                  <label className="text-xs text-gray-600 dark:text-gray-400 block mb-1">Type</label>
                  <div className="flex flex-wrap gap-1.5">
                    {PAIN_TYPES.map(pt => (
                      <button
                        key={pt.id}
                        type="button"
                        onClick={() => handleUpdateEntry(editingRegion, { pain_type: pt.id })}
                        className={`text-xs px-2.5 py-1 border transition-colors ${
                          editingEntry.pain_type === pt.id
                            ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 border-transparent'
                            : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-500'
                        }`}
                      >
                        {pt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-6">
                Tap a body region to log pain
              </div>
            )}

            {/* Selected regions summary */}
            {value.length > 0 && (
              <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                <div className="flex flex-wrap gap-1.5">
                  {value.map(entry => {
                    const region = BODY_REGIONS.find(r => r.id === entry.body_region);
                    return (
                      <button
                        key={entry.body_region}
                        type="button"
                        onClick={() => setEditingRegion(entry.body_region)}
                        className={`text-xs px-2 py-1 flex items-center gap-1 border transition-colors ${
                          editingRegion === entry.body_region
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-gray-300 dark:border-gray-600'
                        }`}
                      >
                        <span
                          className="w-2 h-2 inline-block"
                          style={{ backgroundColor: painColor(entry.pain_level) }}
                        />
                        {region?.label} ({entry.pain_level})
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* "Other" quick-add */}
            {!entryMap.has('other') && (
              <button
                type="button"
                onClick={() => handleRegionTap('other')}
                className="mt-3 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 underline"
              >
                + Add "Other" region
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
